import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Compass, Sparkles } from 'lucide-react'
import { ROUTE_CATALOG } from '@/features/companion/route-catalog'
import { useChatStore } from '@/stores/chat-store'

/**
 * Ask templates — seed the input (via the store draft) so the user finishes
 * the thought and sends. These map to what the companion can genuinely do
 * today; task templates (spawn a research run, build a watchlist) arrive
 * with the async-task phase. One assistant since 2026-10-16, so every
 * template shows: a change request ("Build a scanner for ...") acts.
 */
const ASK_TEMPLATES: Array<{
  id: string
  label: string
  draft: string
}> = [
  {
    id: 'research',
    label: 'Research a ticker',
    draft: 'Do quick research on ',
  },
  {
    id: 'moving',
    label: 'Why is a stock moving?',
    draft: 'Why is this ticker moving today: ',
  },
  {
    id: 'dilution',
    label: 'Check dilution risk',
    draft: 'Check dilution risk for ',
  },
  {
    id: 'patterns',
    label: 'How did similar days perform?',
    draft: 'How did days like this perform historically for ',
  },
  { id: 'help', label: 'How do I use this page?', draft: 'How do I use this page?' },
  {
    id: 'walkthrough',
    label: 'Walk me through this page',
    draft: 'Give me a guided walkthrough of this page',
  },
  {
    id: 'build-scanner',
    label: 'Build a scanner',
    draft: 'Build a scanner for ',
  },
  {
    id: 'build-backtest',
    label: 'Create a backtest',
    draft: 'Create a backtest that ',
  },
]

interface QuickAction {
  id: string
  label: string
  icon: 'ask' | 'navigate'
  run: () => void
}

interface CompanionQuickActionsProps {
  /** Current input text — filters the list; empty shows everything. */
  filter: string
}

/**
 * The quick-actions strip in the floating window's empty state — a command
 * palette over what the companion can already do. Sources are the existing
 * catalogs (route catalog for navigation; ask templates above), deliberately
 * NOT a new hardcoded action system. Typing in the ask bar filters the list;
 * free text that matches nothing simply gets sent as a normal question.
 *
 * Walkthroughs are indexed server-side (get_platform_guide) and join this
 * list when a client-side catalog for them exists.
 */
export function CompanionQuickActions({ filter }: CompanionQuickActionsProps) {
  const navigate = useNavigate()
  const setDraft = useChatStore((s) => s.setDraft)

  const actions = useMemo<QuickAction[]>(() => {
    const asks: QuickAction[] = ASK_TEMPLATES.map((t) => ({
      id: `ask:${t.id}`,
      label: t.label,
      icon: 'ask',
      run: () => setDraft(t.draft),
    }))
    // Parameterless routes only — routes needing a ticker/id are better asked
    // for in words ("open research for AAPL") once navigate_to handles them.
    const navs: QuickAction[] = ROUTE_CATALOG.filter((r) => !r.params?.length).map((r) => ({
      id: `nav:${r.id}`,
      label: `Go to ${r.description}`,
      icon: 'navigate',
      run: () => navigate(r.path),
    }))
    return [...asks, ...navs]
  }, [navigate, setDraft])

  const needle = filter.trim().toLowerCase()
  const visible = needle ? actions.filter((a) => a.label.toLowerCase().includes(needle)) : actions

  if (visible.length === 0) return null

  return (
    <div
      className="max-h-56 overflow-y-auto border-t border-border/50 py-1"
      data-testid="companion-quick-actions"
    >
      <p className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Quick actions
      </p>
      {visible.map((action) => (
        <button
          key={action.id}
          onClick={action.run}
          className="group flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-foreground hover:bg-hover"
        >
          {action.icon === 'ask' ? (
            <Sparkles className="h-3 w-3 shrink-0 text-primary" />
          ) : (
            <Compass className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          <span className="flex-1 truncate">{action.label}</span>
          <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      ))}
    </div>
  )
}
